import axios, { rawAxios } from '@/lib/axios'
import { buildAdminRequestConfig } from '@/lib/organization-scoping'
import { attachNextPricingBoundary } from '@/lib/services/pricingBoundary'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  PriceAdjustmentDisableMode,
  PriceAdjustmentIdInput,
  PriceAdjustmentIndexFilters,
  PriceAdjustmentIndexResult,
  PriceAdjustmentInput,
  PriceAdjustmentPreview,
  PriceAdjustmentPreviewInput,
  PriceAdjustmentResult,
  PriceAdjustmentTimeline,
  PriceAdjustmentTimelineResult,
  ProductPriceAdjustment,
  SavedPriceAdjustmentResult,
} from './priceAdjustments.types'

/** Every price change request is scoped to the organization. */
export interface PriceAdjustmentOrganizationScope {
  organizationId: number | string
}

/** Calls that act on one price change; their Laravel routes require its id. */
export interface PriceAdjustmentScope extends PriceAdjustmentOrganizationScope {
  priceAdjustmentId: PriceAdjustmentIdInput
}

/** Query key entries; the scoped key builders always set the organization as a number. */
interface PriceAdjustmentKeyEntries {
  organizationId: number
}

export interface PriceAdjustmentIndexKeyEntries extends PriceAdjustmentKeyEntries {
  filters?: PriceAdjustmentIndexFilters | undefined
}

/** `priceAdjustmentQueryKeys.show` always sets the id the request URL names. */
export interface PriceAdjustmentShowKeyEntries extends PriceAdjustmentKeyEntries {
  priceAdjustmentId: PriceAdjustmentIdInput
}

export type PriceAdjustmentTimelineKeyEntries = PriceAdjustmentKeyEntries

/** Query functions read only the key. */
type PriceAdjustmentQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

const NEXT_PRICING_BOUNDARY_HEADER = 'x-next-price-adjustment-change-at'

export async function getPriceAdjustments({
  queryKey: [{ organizationId, filters = {} }],
}: PriceAdjustmentQueryContext<PriceAdjustmentIndexKeyEntries>): Promise<PriceAdjustmentIndexResult> {
  const response = await rawAxios.get<ProductPriceAdjustment[]>(
    '/products/price-adjustments',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
  const nextPriceAdjustmentChangeAt: unknown = response.headers[NEXT_PRICING_BOUNDARY_HEADER]

  return attachNextPricingBoundary(response.data, nextPriceAdjustmentChangeAt, { shape: 'array' })
}

export async function getPriceAdjustment({
  queryKey: [{ organizationId, priceAdjustmentId }],
}: PriceAdjustmentQueryContext<PriceAdjustmentShowKeyEntries>): Promise<PriceAdjustmentResult> {
  const response = await rawAxios.get<ProductPriceAdjustment>(
    `/products/price-adjustments/${priceAdjustmentId}`,
    buildAdminRequestConfig({ organizationId })
  )
  const nextPriceAdjustmentChangeAt: unknown = response.headers[NEXT_PRICING_BOUNDARY_HEADER]

  return attachNextPricingBoundary(response.data, nextPriceAdjustmentChangeAt)
}

export async function getPriceAdjustmentTimeline({
  queryKey: [{ organizationId }],
}: PriceAdjustmentQueryContext<PriceAdjustmentTimelineKeyEntries>): Promise<PriceAdjustmentTimelineResult> {
  const response = await rawAxios.get<PriceAdjustmentTimeline>(
    '/products/price-adjustments/timeline',
    buildAdminRequestConfig({ organizationId })
  )
  const nextPriceAdjustmentChangeAt: unknown = response.headers[NEXT_PRICING_BOUNDARY_HEADER]

  return attachNextPricingBoundary(response.data, nextPriceAdjustmentChangeAt)
}

export async function previewPriceAdjustment({
  organizationId,
  ...values
}: PriceAdjustmentPreviewInput & PriceAdjustmentOrganizationScope) {
  return axios.post<PriceAdjustmentPreview>(
    '/products/price-adjustments/preview',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createPriceAdjustment({
  organizationId,
  ...values
}: PriceAdjustmentInput & PriceAdjustmentOrganizationScope) {
  return axios.post<SavedPriceAdjustmentResult>(
    '/products/price-adjustments',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updatePriceAdjustment({
  organizationId,
  priceAdjustmentId,
  ...values
}: PriceAdjustmentInput & PriceAdjustmentScope) {
  return axios.put<SavedPriceAdjustmentResult>(
    `/products/price-adjustments/${priceAdjustmentId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function disablePriceAdjustment({
  organizationId,
  priceAdjustmentId,
  mode,
}: PriceAdjustmentScope & { mode: PriceAdjustmentDisableMode }) {
  return axios.post<ProductPriceAdjustment>(
    `/products/price-adjustments/${priceAdjustmentId}/disable`,
    { mode },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function enablePriceAdjustment({
  organizationId,
  priceAdjustmentId,
}: PriceAdjustmentScope) {
  return axios.post<ProductPriceAdjustment>(
    `/products/price-adjustments/${priceAdjustmentId}/enable`,
    {},
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function resetActivePriceAdjustments({
  organizationId,
}: PriceAdjustmentOrganizationScope) {
  return axios.post<string>(
    '/products/price-adjustments/active/reset',
    {},
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function deletePriceAdjustment({
  organizationId,
  priceAdjustmentId,
}: PriceAdjustmentScope) {
  return axios.delete<string>(
    `/products/price-adjustments/${priceAdjustmentId}`,
    buildAdminRequestConfig({ organizationId })
  )
}
